import { Injectable, inject } from '@angular/core';
import { TranslateService } from '../../core/services/translate.service';
import { VehicleWarning, WarningMessageType } from '../models/vehicle/vehicle-warning';
import { LanguageStore } from '../../core/store';
import { DatePipe } from '@angular/common';

import * as _ from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class WarningMessageHelper {
    private readonly _ts = inject(TranslateService);
    private readonly _languageStore = inject(LanguageStore);



    constructor() {
    }

    //#region WarningMessageType
    public getDescription(warningType: WarningMessageType) {
        switch (warningType) {
            case WarningMessageType.ImmobilizerBypass:
                return this._ts.instant('Warns when the vehicle is used despite no one has logged in to the driver screen');
            case WarningMessageType.SocWarning:
                return this._ts.instant('Warns when the battery level is low on electric vehicles');
            case WarningMessageType.VehicleDelay:
                return this._ts.instant('Warns when the vehicle are at risk of beeing late on the next route');
            case WarningMessageType.NoParkingSpotFound:
                return this._ts.instant('No suitable parking spot is available for this electric vehicle');
            case WarningMessageType.TripNotStartedWarning:
                return this._ts.instant('Warns when the trip has not started');
            case WarningMessageType.OffRouteWarning:
                return this._ts.instant('Warns when the vehicle on a trip is off route');
            case WarningMessageType.SignOnMissingWarning:
                return this._ts.instant('Warns when the vehicle missed its sign on');
            default:
                return '';
        }
    }

    public getTitle(warningType: WarningMessageType) {
        switch (warningType) {
            case WarningMessageType.ImmobilizerBypass:
                return this._ts.instant('Immobilizer Bypass Warnings');
            case WarningMessageType.SocWarning:
                return this._ts.instant('SoC Warnings');
            case WarningMessageType.VehicleDelay:
                return this._ts.instant('Vehicle Delay Warnings');
            case WarningMessageType.NoParkingSpotFound:
                return this._ts.instant('No Parking Spot Found Warnings');
            case WarningMessageType.TripNotStartedWarning:
                return this._ts.instant('Trip Not Started Warnings');
            case WarningMessageType.Other:
                return this._ts.instant('Other');
            case WarningMessageType.OffRouteWarning:
                return this._ts.instant('Off Route Warnings');
            case WarningMessageType.SignOnMissingWarning:
                return this._ts.instant('Signed On Missing Warnings');
            default:
                return this._ts.instant('Other Warnings');
        }
    }

    public getShortHeader(warningType: number) {
        switch (warningType) {
            case WarningMessageType.ImmobilizerBypass:
                return this._ts.instant('Immobilizer Bypass');
            case WarningMessageType.SocWarning:
                return this._ts.instant('Low SoC (State of charge)');
            case WarningMessageType.VehicleDelay:
                return this._ts.instant('Vehicle Delay');
            case WarningMessageType.NoParkingSpotFound:
                return this._ts.instant('No Parking Spot');
            case WarningMessageType.TripNotStartedWarning:
                return this._ts.instant('Trip Not Started');
            case WarningMessageType.Other:
                return this._ts.instant('Other');
            case WarningMessageType.OffRouteWarning:
                return this._ts.instant('Off Route Warnings');
            case WarningMessageType.SignOnMissingWarning:
                return this._ts.instant('Signed On Missing');
            default:
                return this._ts.instant('Other Warnings');
        }
    }
    //#endregion

    //#region VehicleWarning
    public getMessage(warning: VehicleWarning): string {
        const lang = this._languageStore.stateSnapshot;

        if (warning.messageType === WarningMessageType.TripNotStartedWarning) {
            const isEmptyRoute = warning.metadata?.routeName === '0' || warning.metadata?.routeName === 0;
            if (isEmptyRoute) {
                return this._ts.instant('Trip {{tripNumber}} has not started with start time {{tripStartTime}}', {
                    tripNumber: warning.metadata?.tripNumber,
                    tripStartTime: warning.metadata?.tripStartTime
                });
            } else {
                return this._ts.instant('Route {{routeName}}, Trip {{tripNumber}} has not started with start time {{tripStartTime}}', {
                    routeName: warning.metadata?.routeName,
                    tripNumber: warning.metadata?.tripNumber,
                    tripStartTime: warning.metadata?.tripStartTime
                });
            }
        } else if (warning.messageType === WarningMessageType.OffRouteWarning) {
            const isEmptyRoute = warning.metadata?.routeName === '0' || warning.metadata?.routeName === 0;
            if (isEmptyRoute) {
                return this._ts.instant('Vehicle on Trip {{tripNumber}} is off route', {
                    tripNumber: warning.metadata?.tripNumber,
                    tripStartTime: warning.metadata?.tripStartTime
                });
            } else {
                return this._ts.instant('Vehicle on Route {{routeName}}, Trip {{tripNumber}} is off route', {
                    routeName: warning.metadata?.routeName,
                    tripNumber: warning.metadata?.tripNumber,
                    tripStartTime: warning.metadata?.tripStartTime
                });
            }
        }
        if (warning.messageType === WarningMessageType.NoParkingSpotFound) {
            return this._ts.instant('No Parking Spot') + " " + new DatePipe('en-us').transform(new Date(warning.metadata?.startTime), 'dd/MM HH:mm');
        }
        if (warning.messageType === WarningMessageType.SocWarning) {
            return this._ts.instant('Low SoC (State of charge)') + " " + warning.metadata?.sensorValue + " < " + warning.metadata?.planningLimitSoC;
        }
        if (warning.messageType === WarningMessageType.SignOnMissingWarning) {
            return this._ts.instant('Vehicle not signed in');
        }


        if (warning.warningMessages?.length > 0) {
            let msg = '';
            switch (lang) {
                case 'sv_se':
                    msg = _.find(warning.warningMessages, m => m.culture === 0).message;
                    break;
                case 'nb_no':
                    msg = _.find(warning.warningMessages, m => m.culture === 1).message;
                    break;
                case 'da_dk':
                    msg = _.find(warning.warningMessages, m => m.culture === 2).message;
                    break;
                case 'fi_fi':
                    msg = _.find(warning.warningMessages, m => m.culture === 3).message;
                    break;
                default:
                    msg = _.find(warning.warningMessages, m => m.culture === 0).message; // default to Swedish
                    break;
            }

            // If we want any prefix on the messages
            let prefix = '';
            if (warning.messageType === WarningMessageType.ImmobilizerBypass) {
                prefix = new DatePipe('en-us').transform(new Date(warning.measuredAt), 'HH:mm') + ' ';
            }

            return prefix + msg;
        }

        return '';
    }

    public getShortDescription(warning: VehicleWarning): string {
        if (warning.messageType === WarningMessageType.TripNotStartedWarning) {
            return this._ts.instant('{{tripStartTime}} Trip has not started', {
                tripStartTime: warning.metadata?.tripStartTime
            });
        } else if (warning.messageType === WarningMessageType.OffRouteWarning) {
            return this._ts.instant('Vehicle is off route');
        }

        return this.getMessage(warning);
    }
    //#endregion
}
